import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { fetchSettings, fetchMilestones } from "@/lib/api";
import { MochiMeterStrip } from "@/components/MochiMeter";

// Inline rice-ball SVG (same look as the dashboard Feed button — cream
// triangle, black nori strip, tiny kawaii eye dots).
function RiceBallIcon({ size = 28 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
            <ellipse cx="16" cy="28" rx="9" ry="1.6" fill="rgba(27,27,27,0.18)" />
            <path
                d="M16 5 C19 5 22 8 25 17 C26 21 23 25 16 25 C9 25 6 21 7 17 C10 8 13 5 16 5 Z"
                fill="#FDFBF7" stroke="#1B1B1B" strokeWidth="1.6" strokeLinejoin="round"
            />
            <path
                d="M9 19 Q16 23 23 19 L22 24 Q16 26 10 24 Z"
                fill="#2A2A2A" stroke="#1B1B1B" strokeWidth="1"
            />
            <circle cx="13" cy="16" r="1" fill="#1B1B1B" />
            <circle cx="19" cy="16" r="1" fill="#1B1B1B" />
        </svg>
    );
}

// Stats page widget: shows lifetime fed counter, jar inventory, and a
// progress bar to the next milestone tier. Read-only — feeding happens
// elsewhere (the floating Feed button under Mochi).
//
//   <RiceBallJarWidget />
export default function RiceBallJarWidget() {
    const [data, setData] = useState({ jar: 0, fed: 0, meterValue: 0 });
    const [tiers, setTiers] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [s, m] = await Promise.all([fetchSettings(), fetchMilestones()]);
                if (cancelled) return;
                setData({
                    jar: s.rice_balls_in_jar || 0,
                    fed: s.rice_balls_fed_total || 0,
                    meterValue: s.meter_value || 0,
                });
                setTiers(m.tiers || []);
            } catch { /* ignore — widget is non-critical */ }
            finally { if (!cancelled) setLoaded(true); }
        })();
        return () => { cancelled = true; };
    }, []);

    if (!loaded) return null;

    const fed = data.fed;
    const jar = data.jar;

    // Find current tier (largest tier already reached) and next tier
    let currentTier = null;
    let nextTier = null;
    for (const t of tiers) {
        if (fed >= t.tier) currentTier = t;
        else if (!nextTier) nextTier = t;
    }
    const prevValue = currentTier ? currentTier.tier : 0;
    const nextValue = nextTier ? nextTier.tier : (currentTier?.tier ?? 0);
    const progressPct = nextTier
        ? Math.max(0, Math.min(1, (fed - prevValue) / (nextValue - prevValue)))
        : 1;
    const toNext = nextTier ? Math.max(0, nextValue - fed) : 0;

    return (
        <div
            className="bg-cream rounded-clay p-4 my-4 border border-warm-300/40"
            data-testid="riceball-jar-widget"
        >
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-mustard-soft/60 rounded-full flex items-center justify-center shrink-0">
                    <RiceBallIcon size={28} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="font-body text-xs text-warm-500 leading-tight">Mochi's Rice Ball Jar</p>
                    <div className="flex items-baseline gap-2 mt-0.5">
                        <p
                            className="font-display text-3xl text-warm-900 leading-none"
                            data-testid="riceball-fed-total"
                        >
                            {fed}
                        </p>
                        <p className="font-display text-sm text-warm-500">
                            fed · {jar} in jar
                        </p>
                    </div>
                </div>
            </div>

            {nextTier ? (
                <div className="mt-4">
                    <div className="flex items-center justify-between text-[11px] font-body text-warm-500 mb-1.5">
                        <span>
                            {currentTier ? `tier · ${currentTier.tier}` : "no tier yet"}
                        </span>
                        <span data-testid="riceball-next-tier">
                            next · {nextTier.tier}
                        </span>
                    </div>
                    <div className="h-2.5 rounded-full bg-surface overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.round(progressPct * 100)}%` }}
                            transition={{ duration: 0.7, ease: "easeOut" }}
                            className="h-full bg-mustard rounded-full"
                            data-testid="riceball-progress-bar"
                        />
                    </div>
                    <p
                        className="font-body text-[11px] text-warm-500 mt-1.5 text-right italic"
                        data-testid="riceball-to-next"
                    >
                        {toNext} more until "{nextTier.label}"
                    </p>
                </div>
            ) : (
                <p
                    className="font-body text-[12px] text-warm-500 mt-3 text-center italic"
                    data-testid="riceball-max-tier"
                >
                    Top tier unlocked. Mochi is full. 🍡 (lying. give her another.)
                </p>
            )}
   <div className="mt-5 pt-4 border-t border-warm-300/40">
                <MochiMeterStrip meterValue={data.meterValue} />
            </div>
        </div>
    );
}
